import { useState } from 'react';
import {
  Group,
  Header,
  SimpleCell,
  Button,
  Input,
  FormItem,
  NativeSelect,
  Spinner,
  Snackbar,
  Div,
  Text,
} from '@vkontakte/vkui';
import type { StaffMember } from '@pb26/shared';
import { useStaff, useCreateStaff, useUpdateStaff, useDeleteStaff } from '../../api/hooks';

const ROLE_LABEL: Record<'admin' | 'viewer', string> = {
  admin: 'Администратор',
  viewer: 'Наблюдатель (viewer)',
};

function StaffEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: { fullName: string; role: 'admin' | 'viewer' };
  onSave: (data: { fullName: string; role: 'admin' | 'viewer' }) => void;
  onCancel: () => void;
}) {
  const [fullName, setFullName] = useState(initial.fullName);
  const [role, setRole] = useState<'admin' | 'viewer'>(initial.role);

  return (
    <Div style={{ padding: '8px 0' }}>
      <FormItem top="Имя / подпись">
        <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </FormItem>
      <FormItem top="Роль">
        <NativeSelect
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
        >
          <option value="admin">{ROLE_LABEL.admin}</option>
          <option value="viewer">{ROLE_LABEL.viewer}</option>
        </NativeSelect>
      </FormItem>
      <Div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button
          stretched
          className="pb-gradient-btn"
          disabled={!fullName.trim()}
          onClick={() => onSave({ fullName: fullName.trim(), role })}
        >
          Сохранить
        </Button>
        <Button stretched mode="secondary" onClick={onCancel}>
          Отмена
        </Button>
      </Div>
    </Div>
  );
}

export function AdminStaff() {
  const { data, isLoading, refetch } = useStaff();
  const createMut = useCreateStaff();
  const updateMut = useUpdateStaff();
  const deleteMut = useDeleteStaff();

  const [vkUserId, setVkUserId] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('viewer');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const staff = data?.staff ?? [];

  const handleCreate = async () => {
    const id = parseInt(vkUserId.trim(), 10);
    if (!id || id <= 0) {
      setHint('Укажите корректный VK ID');
      return;
    }
    try {
      await createMut.mutateAsync({
        vkUserId: id,
        role,
        fullName: fullName.trim() || undefined,
      });
      setVkUserId('');
      setFullName('');
      setRole('viewer');
      setHint('Доступ добавлен');
      await refetch();
    } catch (e) {
      setHint(e instanceof Error ? e.message : 'Не удалось добавить');
    }
  };

  const handleUpdate = async (
    member: StaffMember,
    patch: { fullName: string; role: 'admin' | 'viewer' },
  ) => {
    try {
      await updateMut.mutateAsync({ id: member.id, ...patch });
      setEditingId(null);
      setHint('Изменения сохранены');
      await refetch();
    } catch (e) {
      setHint(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
  };

  const handleDelete = async (member: StaffMember) => {
    if (member.isSuperadmin || member.id <= 0) return;
    try {
      await deleteMut.mutateAsync(member.id);
      setHint('Доступ удалён');
      await refetch();
    } catch (e) {
      setHint(e instanceof Error ? e.message : 'Не удалось удалить');
    }
  };

  return (
    <>
      <Group header={<Header size="s">Добавить доступ</Header>}>
        <FormItem top="VK ID">
          <Input
            type="number"
            inputMode="numeric"
            value={vkUserId}
            onChange={(e) => setVkUserId(e.target.value)}
            placeholder="105018380"
          />
        </FormItem>
        <FormItem top="Имя (необязательно)">
          <Input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Иван Иванов"
          />
        </FormItem>
        <FormItem top="Роль">
          <NativeSelect
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
          >
            <option value="viewer">{ROLE_LABEL.viewer}</option>
            <option value="admin">{ROLE_LABEL.admin}</option>
          </NativeSelect>
        </FormItem>
        <Div>
          <Button
            size="l"
            stretched
            className="pb-gradient-btn"
            loading={createMut.isPending}
            disabled={!vkUserId.trim()}
            onClick={() => void handleCreate()}
          >
            Добавить
          </Button>
        </Div>
        <SimpleCell multiline>
          <Text className="pb-text-secondary" style={{ fontSize: 13 }}>
            Viewer — просмотр участников и экспорт. Администратор — полный доступ к
            управлению.
          </Text>
        </SimpleCell>
      </Group>

      <Group header={<Header size="s">Администраторы и наблюдатели</Header>}>
        {isLoading ? (
          <Spinner style={{ margin: 16 }} />
        ) : staff.length === 0 ? (
          <SimpleCell>Никого не добавлено</SimpleCell>
        ) : (
          staff.map((member) =>
            editingId === member.id ? (
              <SimpleCell key={member.id || member.vkUserId} multiline>
                <StaffEditor
                  initial={{ fullName: member.fullName, role: member.role }}
                  onSave={(patch) => void handleUpdate(member, patch)}
                  onCancel={() => setEditingId(null)}
                />
              </SimpleCell>
            ) : (
              <SimpleCell
                key={member.id || member.vkUserId}
                subtitle={`VK ${member.vkUserId} · ${ROLE_LABEL[member.role]}${
                  member.isSuperadmin ? ' · суперадмин (.env)' : ''
                }`}
                multiline
                after={
                  member.isSuperadmin || member.id <= 0 ? null : (
                    <Div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Button size="s" mode="outline" onClick={() => setEditingId(member.id)}>
                        Изменить
                      </Button>
                      <Button
                        size="s"
                        mode="outline"
                        appearance="negative"
                        loading={deleteMut.isPending}
                        onClick={() => void handleDelete(member)}
                      >
                        Удалить
                      </Button>
                    </Div>
                  )
                }
              >
                {member.fullName}
              </SimpleCell>
            ),
          )
        )}
      </Group>

      {hint && (
        <Snackbar onClose={() => setHint(null)} duration={3000}>
          {hint}
        </Snackbar>
      )}
    </>
  );
}
